import React, { useState, useEffect } from 'react'
import { getCmsCollection } from '../lib/cms'
import { getOptimizedImageUrl, getImageSrcSet } from '../lib/image'
import Lightbox from './Lightbox'

const DEFAULT_GALLERY_IMAGES = [
  'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/img-1267.jpeg',
  'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/os-7910068-pexels-photo-7910068-auto-compress-cs-tinysrgb-dpr-2-h-650-w-940.jpeg',
  'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/img-1900.jpeg',
  'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/27773421-pexels-photo-27773421-auto-compress-cs-tinysrgb-dpr-2-h-650-w-940.jpeg',
  'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/img-1173.jpeg',
  'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/img-1140.jpeg',
  'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/img-0845.jpeg',
  'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/img-0844.jpeg',
  'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/img-0830.jpeg',
  'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/img-6487.jpeg',
  'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/img-6396.jpeg',
  'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/img-6098.jpeg',
]

export default function FullBleedGalleryPanels({ slug = 'hovedgalleri' }) {
  const [images, setImages] = useState(DEFAULT_GALLERY_IMAGES)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  useEffect(() => {
    let isMounted = true

    async function loadImages() {
      try {
        const items = await getCmsCollection('galleri')
        if (!isMounted) return

        const matched = items.find((item) => {
          const itemSlug = typeof item.slug === 'object' ? item.slug?.da : item.slug
          return itemSlug === slug || item.id === slug
        })

        if (matched?.images?.length > 0) {
          setImages(matched.images)
        } else if (items[0]?.images?.length > 0) {
          setImages(items[0].images)
        }
      } catch (err) {
        console.error('Failed to load gallery panels:', err)
      }
    }

    loadImages()

    return () => {
      isMounted = false
    }
  }, [slug])

  const count = images.length
  if (count === 0) return null

  const openLightbox = (imgIdx) => {
    setLightboxIndex(imgIdx)
    setLightboxOpen(true)
  }

  return (
    <section
      aria-label="Billedpanel oversigt"
      className="relative w-full bg-slate-950 overflow-hidden select-none"
    >
      {/* All gallery images displayed in a balanced responsive grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-0 p-0 m-0">
        {images.map((url, index) => (
          <div
            key={`${url}-${index}`}
            onClick={() => openLightbox(index)}
            className="group relative aspect-4/3 sm:aspect-square md:aspect-4/3 w-full overflow-hidden cursor-pointer bg-slate-900 focus:outline-none"
            role="button"
            tabIndex={0}
            aria-label={`Vis forstørret billede ${index + 1} af ${count}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                openLightbox(index)
              }
            }}
          >
            {/* Background image */}
            <img
              src={getOptimizedImageUrl(url, { width: 800, quality: 80, format: 'auto' })}
              srcSet={getImageSrcSet(url, [350, 600, 850])}
              sizes="(max-width: 640px) 50vw, (max-width: 768px) 33.3vw, 25vw"
              alt=""
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
            />
            {/* Subtle dark overlay that softens on hover, with zoom icon */}
            <div className="absolute inset-0 bg-slate-950/20 group-hover:bg-slate-950/0 transition-colors duration-300 pointer-events-none" />
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
              <span className="w-10 h-10 rounded-full bg-red-600/90 text-white flex items-center justify-center shadow-lg shadow-black/40 transform scale-90 group-hover:scale-100 transition-transform">
                <i className="fa-solid fa-magnifying-glass-plus text-sm" aria-hidden="true" />
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox Modal */}
      <Lightbox
        isOpen={lightboxOpen}
        images={images}
        currentIndex={lightboxIndex}
        title="Galleri"
        onClose={() => setLightboxOpen(false)}
        onNext={() => setLightboxIndex((prev) => (prev + 1) % count)}
        onPrev={() => setLightboxIndex((prev) => (prev - 1 + count) % count)}
      />
    </section>
  )
}
